import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useServiceSelection } from '../contexts/ServiceSelectionContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from './ui/select';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';

import { servicesData, serviceCategories } from '../data/servicesData';
import ServiceBrowserModal from './ServiceBrowserModal';

const Booking = () => {
  const { selectedServices, removeService, clearServices, getTotalDuration } = useServiceSelection();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '', // Will be updated to handle multiple services
    // Honeypot fields
    url: '',      // Honeypot field
    address: ''   // Another honeypot field
  });
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showProgress, setShowProgress] = useState(false);
  const [showServiceBrowser, setShowServiceBrowser] = useState(false);
  const location = useLocation();
  
  // Track when form was loaded for timing-based bot detection
  const formLoadTime = useRef(Date.now());

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'phone') {
      // Remove all non-numeric characters
      const cleaned = value.replace(/\D/g, '');
      
      // Only update if we have 10 digits or less
      if (cleaned.length <= 10) {
        setFormData({ ...formData, [name]: cleaned });
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleServiceChange = (value) => {
    setFormData({ ...formData, service: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setMessageType('');
    
    // Check if form was submitted too quickly (likely a bot)
    const submitTime = Date.now();
    const timeTaken = (submitTime - formLoadTime.current) / 1000; // in seconds
    
    if (timeTaken < 3) {
      setMessage('Please take your time to fill out the form properly.');
      setMessageType('error');
      return;
    }
    
    // Check honeypot fields
    if (formData.url || formData.address) {
      // Silently fail for bots
      setMessage('Appointment booked successfully! You will be contacted soon.');
      setMessageType('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        url: '',
        address: ''
      });
      clearServices();
      setShowProgress(true);
      const timer = setTimeout(() => {
        setMessage('');
        setShowProgress(false);
      }, 8000);
      return;
    }
    
    // Validate phone number has exactly 10 digits
    if (formData.phone.length !== 10) {
      setMessage('Please enter a valid 10-digit phone number');
      setMessageType('error');
      return;
    }

    // Validate at least one service is selected
    if (selectedServices.length === 0) {
      setMessage('Please select at least one service');
      setMessageType('error');
      return;
    }
    
    setIsLoading(true);
    
    // Format services for submission
    const servicesText = selectedServices
      .map(s => `${s.name} (${s.price})`)
      .join(', ');
    
    const submitData = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      service: servicesText, // Send all selected services as a formatted string
      services: selectedServices, // Also send the full array for better backend processing
      // Include honeypot validation data
      _honeypot: {
        url: formData.url,
        address: formData.address,
        timeTaken: timeTaken
      }
    };
  
    try {
      const API_URL = process.env.REACT_APP_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:5000' : '');
      const response = await fetch(`${API_URL}/api/booking`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitData),
      });
      
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.indexOf("application/json") !== -1) {
        const data = await response.json();
        
        if (!response.ok) {
          throw new Error(data.message || 'An error occurred');
        }
        
        console.log('Success:', data);
        setMessage('Appointment booked successfully! You will be contacted soon.');
        setMessageType('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          service: '',
          url: '',
          address: ''
        });
        // Reset form load time
        formLoadTime.current = Date.now();
        clearServices(); // Clear selected services after successful booking
        // Scroll to top to show success message
        window.scrollTo({ top: 0, behavior: 'smooth' });
        
        // Show progress bar and auto-dismiss after 8 seconds
        setShowProgress(true);
        const timer = setTimeout(() => {
          setMessage('');
          setShowProgress(false);
        }, 8000);
        
        // Cleanup timer if user manually closes
        return () => clearTimeout(timer);
      } else {
        const textData = await response.text();
        console.error('Received non-JSON response:', textData);
        throw new Error('Received an invalid response from the server');
      }
    } catch (error) {
      console.error('Error:', error);
      setMessage(error.message || 'Error booking appointment. Please try again.');
      setMessageType('error');
    } finally {
      setIsLoading(false);
    }
  };



  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50/50 to-white">
      {/* Hero Section with Ocean Theme */}
      <div className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-cyan-600 to-blue-700">
        {/* Animated background patterns */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 opacity-30" style={{
            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,.1) 35px, rgba(255,255,255,.1) 70px)',
          }}></div>
          <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyan-400/20 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
        </div>
        
        <div className="relative container mx-auto px-4 py-24">
          <div className="flex flex-col items-center justify-center text-center space-y-6">
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white font-serif">
              Book Your Appointment
            </h1>
            <p className="text-xl text-blue-100 max-w-3xl">
              Choose your services below and we'll contact you to schedule an appointment.
            </p>
          </div>
        </div>
        
        {/* Wave separator */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg className="w-full h-16" viewBox="0 0 1440 100" fill="#f0f9ff">
            <path d="M0,50 C360,100 720,0 1440,50 L1440,100 L0,100 Z"></path>
          </svg>
        </div>
      </div>

      {/* Main Booking Content */}
      <div className="container mx-auto px-4 py-12 -mt-8 relative z-10">
        <div className="max-w-5xl mx-auto">
          {/* Success Modal Overlay */}
          {message && messageType === 'success' && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
              <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative transform animate-scale-in">
                <button
                  onClick={() => {
                    setMessage('');
                    setShowProgress(false);
                  }}
                  className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  ✕
                </button>
                
                <div className="text-center space-y-6">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-800 mb-2">Appointment Request Received</h3>
                    <p className="text-gray-600">
                      We've received your appointment request and will contact you within 24 hours to confirm your preferred time.
                    </p>
                  </div>
                  
                  <Button
                    onClick={() => {
                      setMessage('');
                      setShowProgress(false);
                    }}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all"
                  >
                    Continue
                  </Button>
                </div>
              </div>
            </div>
          )}
          
          {/* Error Message */}
          {message && messageType === 'error' && (
            <Alert className="mb-6 bg-red-50 border-red-200">
              <AlertDescription className="text-red-800">
                {message}
              </AlertDescription>
            </Alert>
          )}

          {/* Two-Column Layout */}
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Left Column - Booking Form */}
            <div className="lg:col-span-2">
              <Card className="border-0 shadow-xl bg-white/95 backdrop-blur-sm">
                <CardHeader className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-t-lg">
                  <CardTitle className="text-3xl font-serif text-gray-900">
                    Contact Information
                  </CardTitle>
                  <p className="text-gray-600 mt-2">Please provide your contact details</p>
                </CardHeader>
                <CardContent className="p-8">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Personal Information */}
                    <div className="space-y-4">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="name" className="text-gray-700 font-medium">
                            Full Name *
                          </Label>
                          <Input
                            type="text"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            placeholder="John Doe"
                            className="h-12 border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="phone" className="text-gray-700 font-medium">
                            Phone Number *
                          </Label>
                          <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            required
                            placeholder="5551234567"
                            maxLength="10"
                            className="h-12 w-full px-3 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                          />
                          <p className="text-xs text-gray-500">Enter 10-digit phone number</p>
                          {formData.phone && formData.phone.length > 0 && (
                            <p className="text-sm text-blue-600 font-medium">
                              Formatted: {formData.phone.length >= 6 
                                ? `(${formData.phone.slice(0, 3)}) ${formData.phone.slice(3, 6)}-${formData.phone.slice(6, 10)}`
                                : formData.phone.length >= 3
                                ? `(${formData.phone.slice(0, 3)}) ${formData.phone.slice(3)}`
                                : `(${formData.phone}`
                              }
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-gray-700 font-medium">
                          Email Address *
                        </Label>
                        <Input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          placeholder="john@example.com"
                          className="h-12 border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                        />
                      </div>
                      
                      {/* Honeypot field 1 - Hidden from users */}
                      <div style={{ position: 'absolute', left: '-9999px', height: '0', width: '0', overflow: 'hidden' }}>
                        <Label htmlFor="url">
                          URL (Leave empty)
                        </Label>
                        <Input
                          type="text"
                          id="url"
                          name="url"
                          value={formData.url}
                          onChange={handleChange}
                          tabIndex="-1"
                          autoComplete="off"
                        />
                      </div>
                      
                      {/* Honeypot field 2 - Hidden from users */}
                      <div style={{ position: 'absolute', left: '-9999px', height: '0', width: '0', overflow: 'hidden' }}>
                        <Label htmlFor="address">
                          Address (Leave empty)
                        </Label>
                        <Input
                          type="text"
                          id="address"
                          name="address"
                          value={formData.address}
                          onChange={handleChange}
                          tabIndex="-1"
                          autoComplete="off"
                        />
                      </div>
                    </div>

                    <Separator className="my-6" />

                    {/* Service Selection */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-gray-800">
                        Selected Services
                      </h3>
                      
                      {/* Selected Services Display */}
                      {selectedServices.length > 0 ? (
                        <div className="space-y-3">
                          <div className="bg-blue-50 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-3">
                              <p className="text-sm font-medium text-blue-900">
                                {selectedServices.length} service{selectedServices.length > 1 ? 's' : ''} selected
                              </p>
                              <p className="text-sm font-medium text-blue-900">
                                Total duration: {getTotalDuration()} minutes
                              </p>
                            </div>
                            <div className="space-y-2">
                              {selectedServices.map((service) => (
                                <div
                                  key={service.id}
                                  className="flex items-center justify-between bg-white p-3 rounded-md shadow-sm"
                                >
                                  <div className="flex-1">
                                    <p className="font-medium text-gray-800">{service.name}</p>
                                    <div className="flex items-center gap-3 text-sm text-gray-600">
                                      <span className="text-gray-600">
                                        {service.duration}
                                      </span>
                                      <span className="font-semibold text-blue-600">
                                        {service.price}
                                      </span>
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => removeService(service.id)}
                                    className="text-red-500 hover:text-red-700 transition-colors px-3 py-1 text-sm"
                                  >
                                    Remove
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                          
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setShowServiceBrowser(true)}
                            className="w-full border-blue-300 text-blue-600 hover:bg-blue-50"
                          >
                            Add More Services
                          </Button>
                        </div>
                      ) : (
                        <div className="text-center py-8 px-4 bg-gray-50 rounded-lg">
                          <p className="text-gray-600 mb-4">
                            No services selected yet
                          </p>
                          <Button
                            type="button"
                            onClick={() => setShowServiceBrowser(true)}
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                          >
                            Browse Services
                          </Button>
                        </div>
                      )}
                    </div>

                    {/* Submit Button */}
                    <Button 
                      type="submit" 
                      disabled={isLoading}
                      className="w-full h-14 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300"
                    >
                      {isLoading ? 'Processing...' : 'Submit Request'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Information */}
            <div className="space-y-6">
              {/* Contact Info Card */}
              <Card className="border-0 shadow-lg bg-gradient-to-br from-blue-50 to-cyan-50">
                <CardHeader>
                  <CardTitle className="text-xl font-serif">
                    Location & Hours
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="font-medium text-gray-800">Office Hours</p>
                    <p className="text-sm text-gray-600">Monday - Friday: 9am - 5pm</p>
                    <p className="text-sm text-blue-600 font-medium mt-1">By Appointment Only</p>
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">Phone</p>
                    <a href="tel:5619906745" className="text-sm text-blue-600 hover:underline">
                      (561) 990-6745
                    </a>
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">Email</p>
                    <a href="mailto:info@oceaniquebodycare.com" className="text-sm text-blue-600 hover:underline">
                      info@oceaniquebodycare.com
                    </a>
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">Address</p>
                    <p className="text-sm text-gray-600">
                      7025 Beracasa Way, 103C<br />
                      Boca Raton, FL 33433
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* What to Expect */}
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-xl font-serif">Your Visit</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 text-sm text-gray-700">
                    <p>
                      We'll discuss your goals and create a personalized treatment plan during your consultation.
                    </p>
                    <p>
                      Our spa offers a comfortable environment with modern equipment for your treatments.
                    </p>
                    <p>
                      All treatments are performed by trained and certified aesthetic professionals.
                    </p>
                  </div>
                </CardContent>
              </Card>


            </div>
          </div>
        </div>
      </div>



      {/* Service Browser Modal */}
      <ServiceBrowserModal 
        isOpen={showServiceBrowser} 
        onClose={() => setShowServiceBrowser(false)} 
      />
    </div>
  );
};

export default Booking;